<template>
  <!-- Static header/paragraph from the form builder. -->
  <component :is="field.tag === 'h2' || field.tag === 'h3' ? field.tag : 'p'"
             v-if="field.type === 'static'"
             class="lp-form-static"
             v-html="field.html" />

  <!-- Conditionally-shown companion fields (e.g. other_state). -->
  <div v-else-if="!visible" style="display:none"></div>

  <ValidationProvider v-else :vid="vid" :name="field.label || field.name" :rules="rules" v-slot="{ errors }">
    <!-- Textual inputs -->
    <lp-input
      v-if="isInput"
      :label="labelText"
      :type="inputType"
      v-model="innerValue"
      :placeholder="field.placeholder || ''"
      :error="errors[0]"
      @blur="$emit('blur')"
    />

    <!-- Select (single or native multi-select) -->
    <lp-select
      v-else-if="field.type === 'select'"
      :label="labelText"
      v-model="innerValue"
      :options="field.options"
      :multiple="!!field.multiple"
      :placeholder="field.placeholder || 'Select…'"
      :error="errors[0]"
    />

    <!-- Radio group -->
    <div v-else-if="field.type === 'radio'" class="lp-field-group">
      <span class="lp-field-group-label">{{ labelText }}</span>
      <div v-if="field.help" class="lp-field-help" v-html="field.help"></div>
      <lp-radio
        v-for="opt in field.options"
        :key="opt.value"
        v-model="innerValue"
        :native-value="opt.value"
        :label="opt.label"
      />
      <span v-if="errors[0]" class="field-error">{{ errors[0] }}</span>
    </div>

    <!-- Checkbox group (multi) or single checkbox (agree / receive_updates) -->
    <div v-else-if="field.type === 'checkbox'" class="lp-field-group">
      <span v-if="showGroupLabel" class="lp-field-group-label">{{ labelText }}</span>
      <div v-if="field.help" class="lp-field-help" v-html="field.help"></div>
      <lp-checkbox
        v-for="opt in field.options"
        :key="opt.value"
        v-model="innerValue"
        :value="opt.value"
        :label="optionLabel(opt)"
      />
      <span v-if="errors[0]" class="field-error">{{ errors[0] }}</span>
    </div>
  </ValidationProvider>
</template>

<script>
// Renders one form-builder schema field with the lp-* primitives and wires it
// to vee-validate. The parent owns a `values` object keyed by field name and
// updates it from this component's `input` events.
export default {
  name: 'lp-form-field',
  props: {
    field: { type: Object, required: true },
    value: { default: null },
    values: { type: Object, default: () => ({}) },
    // Unique-per-attendee suffix so ValidationProvider vids don't collide.
    scope: { type: [String, Number], default: '' },
  },
  data() {
    // Local v-model target — vee-validate's ValidationProvider tracks the
    // field's value through a real reactive property (a computed with a setter
    // is not detected as a model), synced to/from the parent via watchers.
    // (computed props aren't initialized yet in data(), so derive the flag here)
    const isArrayValued = this.field.type === 'checkbox'
      || (this.field.type === 'select' && !!this.field.multiple)

    return {
      innerValue: isArrayValued
        ? (Array.isArray(this.value) ? this.value.slice() : [])
        : this.value,
    }
  },
  watch: {
    value(v) {
      const next = this.isArrayValued ? (Array.isArray(v) ? v : []) : v
      if (JSON.stringify(next) !== JSON.stringify(this.innerValue)) this.innerValue = next
    },
    innerValue(v) {
      this.$emit('input', v)
    },
  },
  computed: {
    vid() { return `${this.field.name}-${this.scope}` },
    // Fields whose value is a list: checkbox groups and native multi-selects.
    isArrayValued() {
      return this.field.type === 'checkbox' || (this.field.type === 'select' && !!this.field.multiple)
    },
    isInput() {
      return ['text', 'email', 'number', 'tel', 'date', 'textarea'].includes(this.field.type)
    },
    inputType() {
      if (this.field.type === 'textarea') return 'text'
      if (this.field.name === 'phone_number') return 'tel'
      return this.field.type === 'number' ? 'number' : (this.field.type === 'email' ? 'email' : 'text')
    },
    labelText() {
      return this.field.required ? `${this.field.label} *` : this.field.label
    },
    showGroupLabel() {
      // Only the two hardcoded consent checkboxes (agree / receive_updates) read
      // their label from the option itself — everything else, including
      // organizer-authored single-option checkbox groups, must show its label.
      return !['agree', 'receive_updates'].includes(this.field.name)
    },
    arrayValue() {
      return Array.isArray(this.value) ? this.value : []
    },
    rules() {
      const r = {}
      if (this.field.required) r.required = true
      if (this.field.type === 'email') r.email = true
      // Compare against the current email value (reactive) rather than a
      // cross-field @vid reference.
      if (this.field.name === 'email_confirmation') r.matches = { other: this.values.email || '' }
      if (this.field.max) r.max = this.field.max
      return r
    },
    visible() {
      const cond = this.field.show_when
      if (!cond) return true
      return String(this.values[cond.field] ?? '') === String(cond.equals)
    },
  },
  methods: {
    // The consent checkboxes (agree / receive_updates) hide the group label —
    // the one that carries the required asterisk — so a required one has to
    // mark the option itself. Everything else already shows it on the group.
    optionLabel(opt) {
      return (!this.showGroupLabel && this.field.required) ? `${opt.label} *` : opt.label
    },
  },
}
</script>

<style lang="scss">
.lp-form-static {
  margin: 18px 0 4px;
  color: var(--brand-deep);
}
h2.lp-form-static { font-size: var(--fs-h5); font-weight: 500; }
h3.lp-form-static { font-size: var(--fs-lead); font-weight: 500; }
.lp-field-group { margin-bottom: 14px; }
.lp-field-group-label {
  display: block;
  font-size: var(--fs-small);
  font-weight: 500;
  color: var(--text);
  margin-bottom: 8px;
}
.lp-field-group .field-error {
  display: block;
  font-size: var(--fs-label);
  color: var(--danger);
  margin-top: 4px;
}
/* Field help/description text (form-builder help_block), e.g. an accessibility
   note above a "Contact Me" checkbox or a refund policy above "I acknowledge". */
.lp-field-help {
  font-size: var(--fs-small);
  line-height: 1.6;
  color: var(--text-secondary);
  margin-bottom: 10px;
}
.lp-field-help p:last-child { margin-bottom: 0; }
.lp-field-help a { color: var(--brand-deep); text-decoration: underline; }
</style>
